import { NetworkStatus } from '@apollo/client'
import { Tabs } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { useGetReportsByCountryAndType } from '../../../graphql/reports/useQueriesReport'
import { Country, ReportType } from '../../../types'
import { allCountriesOption, noFilterOption, useReportStore } from '../ReportStore'
import { ReportTab } from '../types'
import { BigReportsTable } from './BigReportsTable'
import { MeetingReportsTable } from './MeetingReportsTable'
import { PyramidReportsTable } from './PyramidReportsTable'
import { RankingReportsView } from './RankingReportView'
import { ShowMoreButton } from './ShowMoreButton'

const { TabPane } = Tabs

export const ReportsView: FC = () => {
  const { t } = useTranslation()
  const [activeTab, countries, selectedCountry] = useReportStore(
    (state) => [state.activeTab, state.countries, state.selectedCountry],
    shallow,
  )

  const skipQuery = activeTab === ReportTab.RankingReport
  const { data, error, loading, fetchMore, networkStatus } = useGetReportsByCountryAndType(
    selectedCountry.value === noFilterOption.value
      ? (countries as Country[])
      : selectedCountry.value === allCountriesOption.value
      ? [Country.All]
      : [selectedCountry.value as Country],
    activeTab === ReportTab.BigReport
      ? ReportType.Detailed
      : activeTab === ReportTab.MeetingReport
      ? ReportType.Meeting
      : activeTab === ReportTab.PyramidReport
      ? ReportType.Pyramid
      : ReportType.Ranking,
    skipQuery,
  )

  const reportsData = data?.findReportsInDownloadsViewByCountryAndType

  const loadMore = () => {
    if (!reportsData?.hasNextPage) {
      return
    }
    fetchMore({
      variables: {
        pagination: {
          cursorReportId: reportsData.endCursorReportId,
          cursorReportDate: reportsData.endCursorReportDate,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult
        }
        const previousEdges = previousResult.findReportsInDownloadsViewByCountryAndType.edges
        const fetchMoreEdges = fetchMoreResult.findReportsInDownloadsViewByCountryAndType.edges
        fetchMoreResult.findReportsInDownloadsViewByCountryAndType.edges = [...previousEdges, ...fetchMoreEdges]
        return { ...fetchMoreResult }
      },
    })
  }

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore
  const showMoreLayoutActive = !!reportsData?.hasNextPage && (!loading || isLoadingMore)
  const showMoreFooter = <ShowMoreButton visible={showMoreLayoutActive} loadingMore={isLoadingMore} onLoadMore={loadMore} />

  if (!skipQuery && (error || !data || (loading && !isLoadingMore))) {
    return (
      <BackgroundBox r height="withTabs">
        <NewLoadingAndErrors isLoading={loading && !isLoadingMore} hasNoData={!data} error={error} showSpinner t={t} />
      </BackgroundBox>
    )
  }

  return (
    <>
      <Tabs activeKey={activeTab} renderTabBar={() => <></>}>
        {activeTab === ReportTab.MeetingReport && (
          <TabPane key={ReportTab.MeetingReport}>
            <MeetingReportsTable
              reports={data!.findReportsInDownloadsViewByCountryAndType.edges}
              showMoreButtonVisible={showMoreLayoutActive}
            />
            {showMoreFooter}
          </TabPane>
        )}
        {activeTab === ReportTab.BigReport && (
          <TabPane key={ReportTab.BigReport}>
            <BigReportsTable
              reports={data!.findReportsInDownloadsViewByCountryAndType.edges}
              showMoreButtonVisible={showMoreLayoutActive}
            />
            {showMoreFooter}
          </TabPane>
        )}
        {activeTab === ReportTab.RankingReport && (
          <TabPane key={ReportTab.RankingReport}>
            <RankingReportsView />
          </TabPane>
        )}
        {activeTab === ReportTab.PyramidReport && (
          <TabPane key={ReportTab.PyramidReport}>
            <PyramidReportsTable
              reports={data!.findReportsInDownloadsViewByCountryAndType.edges}
              showMoreButtonVisible={showMoreLayoutActive}
            />
            {showMoreFooter}
          </TabPane>
        )}
      </Tabs>
    </>
  )
}
